import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  ISearchResultItemGeneric,
  SearchFunction
} from '@shared/components/search/multiple-select-with-search/multiple-select-with-search.component';
import { GetRand } from '@shared/utils/mix.utils';
import { asyncScheduler, BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, throttleTime } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-search-field',
  templateUrl: './search-field.component.html',
  styleUrls: [ './search-field.component.scss' ],
})
export class SearchFieldComponent<T> implements OnInit, OnChanges, AfterViewInit {

  @Input() passedFormControl: UntypedFormControl;
  @Input() searchFn: SearchFunction<T>;
  @Input() enableValidation: boolean;
  @Input() placeholder: string = $localize`Поиск`;

  /** only controlPropName property of selected ISearchResultItem will be set to control value **/
    // @Input() valuePropName: 'id' | 'name';
  @Input() messageAsResult: string;
  @Input() closeTrigger$: Observable<any>;
  @Input() resetTrigger$: Observable<any>;
  @Input() label: string;
  @Input() labelAnimation: boolean;
  @Input() isRequired: boolean;
  @Input() isDisabled: boolean;
  @Input() onlyErrorBorder: boolean = true;
  @Input() autoselect: boolean;
  @Input() triggerIcon = 'keyboard_arrow_down';

  @Output() selected = new EventEmitter<ISearchResultItemGeneric<T>>();

  @ViewChild('panelMenuTrigger') panelMenuTrigger: MatMenuTrigger;
  @ViewChild('labelElement') labelElement: ElementRef<HTMLElement>;
  @ViewChild('searchInputEl') searchInputEl: ElementRef<HTMLElement>;
  searchControl = new UntypedFormControl();
  list$ = new BehaviorSubject<ISearchResultItemGeneric<T>[]>([]);
  invalid = false;
  private selectedItem: ISearchResultItemGeneric<T>;
  isLoading = false;
  searchHasBeenDone = false;
  showPanel = false;
  searchTrigger$ = new Subject();
  panelWidth: any;
  randomName = 'field-' + GetRand();
  showFixedLabel: boolean;
  showAnimatedLabel: boolean;
  panelOpened$ = new BehaviorSubject<boolean>(false);
  menuMarkerClass = 'menu_marker_' + GetRand();

  constructor() {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isDisabled) {
      if (this.isDisabled) {
        this.searchControl.disable();
      } else {
        this.searchControl.enable();
      }
    }
  }

  ngOnInit(): void {
    if (!this.passedFormControl) {
      console.error('SearchField: passedFormControl not found');
      return;
    }

    this.showFixedLabel = Boolean(this.label || (this.labelAnimation && this.placeholder));
    this.showAnimatedLabel = Boolean(this.labelAnimation && (this.label || this.placeholder));

    this.subscriveToSearchTrigger();

    if (this.passedFormControl.value) {
      this.setSelectedValue(this.passedFormControl.value);
    }

    this.passedFormControl.valueChanges.pipe(untilDestroyed(this)).subscribe({
      next: (data) => {
        if (!data) {
          this.searchControl.reset();
          return;
        }
        if (data?.name && data.name !== this.searchControl.value) {
          this.searchControl.setValue(data.name);
          return;
        }
      }
    });

    if (this.closeTrigger$) {
      this.closeTrigger$.pipe(untilDestroyed(this)).subscribe({
        next: (data) => {
          // console.log('Close trigger', this.label || this.placeholder);
          this.closePanel();
        },
      });
    }

    if (this.searchFn) {
      this.subscribeToSeachChange();
    }

    this.setSearchControlValidators();

    // PatchControl(this.passedFormControl, this.markSearchAsTouched.bind(this),
    //   this.markSearchAsUntouched.bind(this));
  }

  private markSearchAsTouched() {
    this.searchControl.markAsTouched();
  }

  private markSearchAsUntouched() {
    this.searchControl.markAsUntouched();
  }

  ngAfterViewInit() {
    // if (this.panelMenuTrigger) {
    //   this.panelMenuTrigger.menuOpened.subscribe({
    //     next: (data) => {
    //       this.panelOpened$.next(true);
    //     }
    //   });
    //   this.panelMenuTrigger.menuClosed.subscribe({
    //     next: (data) => {
    //       setTimeout(() => {
    //         if (!this.panelMenuTrigger.menuOpen) {
    //           this.panelOpened$.next(false);
    //         }
    //       }, 150)
    //     }
    //   });
    // }
  }

  controlValueChangedOutside(val: ISearchResultItemGeneric<T>) {
    if (!val?.id) {
      // control has been cleaned in parent component
      this.searchControl.reset();
      this.selectedItem = null;
    } else if (val?.id && val?.name) {
      if (val.name !== this.searchControl.value) {
        // control value has been set in parent component
        this.searchControl.setValue(val.name, { emitEvent: false });
      }
      this.invalid = false;
    }
  }

  private setSearchControlValidators() {
    const validators = [];
    if (this.isRequired) {
      validators.push(Validators.required);
    }
    // if (this.allowOnlySelectedValue) {
    //   const val: ValidatorFn = (control) => {
    //
    //   }
    // }
    this.searchControl.setValidators(validators);
    this.searchControl.updateValueAndValidity();
  }

  private openPanel() {
    this.updatePanelWidth();
    this.showPanel = true;
    this.panelMenuTrigger.openMenu();
    this.searchTrigger$.next();
  }

  private closePanel() {
    this.showPanel = false;
    this.panelMenuTrigger.closeMenu();
  }

  private updatePanelWidth() {
    if (this.labelElement?.nativeElement) {
      this.panelWidth = this.labelElement.nativeElement.clientWidth;
    }
  }

  private subscriveToSearchTrigger() {
    this.searchTrigger$.pipe(
      map(() => this.searchControl?.value),
      switchMap((v) => {
        this.list$.next([]);
        this.isLoading = true;
        return this.searchFn(v).pipe(
          catchError(e => {
            console.error(e);
            this.isLoading = false;
            return of([]);
          })
        );
      }),
      catchError((e) => {
        this.isLoading = false;
        return of([]);
      }),
    ).subscribe({
      next: (data) => {
        if (data?.length === 1 && data[0].isLoading) {
          this.isLoading = true;
        } else {
          this.searchHasBeenDone = true;
          this.isLoading = false;
          this.list$.next(data);
        }
      }
    });
  }

  private subscribeToSeachChange() {
    this.searchControl.valueChanges
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        throttleTime(400, asyncScheduler, { leading: true, trailing: true }),
        catchError((e) => {
          return of([]);
        }),
      )
      .subscribe({
        next: () => this.searchTrigger$.next()
      });
  }

  setInvalid() {
    this.invalid = true;
  }

  onSearchFocus(event: any) {
    return;
    // this.searchControl.setValue('');
    // if (!this.autocompleteTrigger?.panelOpen) {
    //   this.autocompleteTrigger?.openPanel();
    //   this.lastPanelOpenTime = Date.now();
    // } else {
    //   const now = Date.now();
    //   // prevent panel blinking
    //   if (this.lastPanelOpenTime && now > this.lastPanelOpenTime + 100) {
    //     this.autocompleteTrigger?.closePanel();
    //   }
    // }
  }

  onSearchBlur() {
    // if user wrote a name and didn't select any suggesions, assuming that first one is what he or she needs,
    // we should auto select first one
    if (this.autoselect && this.searchControl.value && !this.selectedItem) {
      const list = this.list$.getValue();
      if (list.length) {
        this.setSelectedValue(list[0]);
      }
    }

    // setTimeout(() => {
    //   this.invalid = this._formControl.invalid;
    // }, 0)
  }

  private setValueToPassedControl(value) {
    if (this.passedFormControl.value === value) {
      return;
    }
    this.passedFormControl.setValue(value);
  }

  private setSelectedValue(value: ISearchResultItemGeneric<T>) {
    this.searchControl.setValue(value?.name || '');
    this.selectedItem = value;
    let valueForControl: any = value;
    // if (this.valuePropName && value) {
    //   valueForControl = value[this.valuePropName];
    // }
    // console.log(1111, valueForControl);
    this.setValueToPassedControl(valueForControl);
    this.invalid = false;
    this.selected.emit(value);
  }

  iconClick($event) {
    if (this.isDisabled) {
      return;
    }
    if (this.searchControl.value) {
      $event.preventDefault();
      $event.stopPropagation();
      this.setSelectedValue(null);
      return;
    }
    this.fieldClick(null);
  }

  fieldClick($event: MouseEvent) {
    if (this.isDisabled) {
      return;
    }
    this.passedFormControl.markAsTouched();
    this.passedFormControl.updateValueAndValidity();
    this.searchControl.setValue(this.searchControl.value);
    if (!this.panelMenuTrigger.menuOpen) {
      this.openPanel();
    } else {
      // todo ask Igor - this line causes unnecessary closing of the mat-menu when clicking
      //  on the "label" (when clicking on the "input" it works correctly)
      // this.closePanel();
    }
  }

  optionSelected(item: ISearchResultItemGeneric<T>) {
    this.setSelectedValue(item);
  }
}
